import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Brain,
    Loader2,
    Sparkles,
    Music2,
    Instagram,
    Facebook,
    Lock,
    Crown,
} from "lucide-react";
import { toast } from "sonner";

import { useSession } from "@/contexts/SessionContext";
import { api } from "@/lib/api";

const PLATFORM_ICON = {
    tiktok: Music2,
    instagram: Instagram,
    facebook: Facebook,
};
const PLATFORM_LABEL = {
    tiktok: "TikTok",
    instagram: "Instagram",
    facebook: "Facebook",
};
const CONFIDENCE_TINT = {
    high: "border-[#00F5D4]/70 bg-[#00F5D4]/10 text-[#00F5D4]",
    medium: "border-[#2F58CD]/60 bg-[#2F58CD]/10 text-[#7DA1FF]",
    low: "border-[#FF5470]/50 bg-[#FF5470]/10 text-[#FF5470]",
};

export default function Agent2Card({ assetId, platforms = [], onUseSlot }) {
    const { profile, balance, setBalanceFast, openDrawer, tier, openSubscriptionDrawer } =
        useSession();
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const nicheEmpty = !profile?.niche || profile.niche.trim() === "";
    const isPro = tier === "pro";

    // ------------------------------------------------------------------
    // Tier-locked card — Agent 2 is Pro-only. Render a passive upsell that
    // never calls the backend. The single CTA opens the SubscriptionDrawer.
    // ------------------------------------------------------------------
    if (!isPro) {
        return (
            <motion.section
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45 }}
                className="relative flex flex-col gap-4 overflow-hidden rounded-2xl border border-dashed border-[#1F2A40] bg-[#111726]/60 p-6"
                data-testid="agent2-card"
                data-locked="true"
            >
                <div
                    aria-hidden
                    className="pointer-events-none absolute inset-0 opacity-60"
                    style={{
                        background:
                            "radial-gradient(circle at 90% 0%, rgba(0,245,212,0.08) 0%, transparent 60%)",
                    }}
                />
                <div className="relative flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                        <Brain className="h-4 w-4 text-[#8A93A6]" />
                        <span className="text-[10px] uppercase tracking-[0.32em] text-[#8A93A6]">
                            agent 2 — smart recommendation
                        </span>
                    </div>
                    <span
                        className="inline-flex items-center gap-1 rounded-full border border-[#00F5D4]/40 bg-[#00F5D4]/10 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.24em] text-[#00F5D4]"
                        data-testid="agent2-pro-badge"
                    >
                        <Crown className="h-3 w-3" strokeWidth={2.4} />
                        pro
                    </span>
                </div>

                <div className="relative flex flex-col gap-3">
                    <div className="flex items-start gap-3">
                        <div
                            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[#1F2A40] bg-[#0B0F19] text-[#00F5D4]"
                            style={{
                                boxShadow:
                                    "0 0 14px rgba(0,245,212,0.18), inset 0 0 0 1px rgba(0,245,212,0.25)",
                            }}
                            data-testid="agent2-lock-icon"
                        >
                            <Lock className="h-4 w-4" strokeWidth={2.2} />
                        </div>
                        <div className="flex flex-col gap-1">
                            <p className="text-sm font-medium text-[#E8ECF5]">
                                Audience Predict — a Pro feature
                            </p>
                            <p className="max-w-prose text-sm leading-relaxed text-[#8A93A6]">
                                Agent 2 cross-references your niche, target
                                demographics and platform behavior data to
                                surface the optimal post slot. Unlock it with a
                                Pro plan.
                            </p>
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={() =>
                            openSubscriptionDrawer({
                                code: "tier_required",
                                required: "pro",
                                feature: "Audience Predict",
                            })
                        }
                        className="group inline-flex w-fit items-center gap-2 rounded-lg border border-[#00F5D4]/30 bg-gradient-to-r from-[#2F58CD] to-[#2F58CD] px-4 py-2 text-sm font-medium text-white transition-all hover:border-[#00F5D4]/60 hover:shadow-[0_0_22px_rgba(0,245,212,0.35)]"
                        data-testid="agent2-unlock-btn"
                    >
                        <Crown className="h-3.5 w-3.5" />
                        Unlock with Pro
                    </button>

                    <p
                        className="font-mono text-[11px] italic text-[#8A93A6]"
                        data-testid="agent2-locked-footer"
                    >
                        Pro · Audience Predict + Adsum QC · $79/mo simulated
                    </p>
                </div>
            </motion.section>
        );
    }

    // ------------------------------------------------------------------
    // Pro users — full Agent 2 experience.
    // ------------------------------------------------------------------
    const run = async () => {
        if (nicheEmpty) {
            openDrawer("profile");
            return;
        }
        setLoading(true);
        setResult(null);
        try {
            const { data } = await api.post("/agent2/recommend", {
                asset_id: assetId,
                platforms,
            });
            setResult(data);
            if (typeof data.balance_after === "number") {
                setBalanceFast(data.balance_after);
            }
            toast.success("Agent 2 done", {
                description: `Best: ${data.best_overall?.day_of_week} at ${data.best_overall?.time_local}`,
            });
        } catch (err) {
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail;
            if (status === 402) {
                toast.error("Vault is low", {
                    description: "Top up to keep using Agent 2.",
                });
                openDrawer("topup");
            } else if (status === 403) {
                // Centralized interceptor already opened the drawer; no toast.
            } else {
                toast.error("Agent 2 failed", {
                    description:
                        typeof detail === "string"
                            ? detail
                            : detail?.error || err?.message,
                });
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="flex flex-col gap-4 rounded-2xl border border-dashed border-[#1F2A40] bg-[#111726]/60 p-6"
            data-testid="agent2-card"
            data-locked="false"
        >
            <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <Brain className="h-4 w-4 text-[#00F5D4]" />
                    <span className="text-[10px] uppercase tracking-[0.32em] text-[#8A93A6]">
                        agent 2 — smart recommendation
                    </span>
                </div>
                <span className="font-mono text-[10px] uppercase tracking-wider text-[#8A93A6]/80">
                    audience predict
                </span>
            </div>

            <AnimatePresence mode="wait">
                {!result && !loading && (
                    <motion.div
                        key="default"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="flex flex-col items-start gap-3"
                    >
                        <div className="flex items-center gap-2">
                            <motion.span
                                animate={{ opacity: [0.4, 1, 0.4] }}
                                transition={{ duration: 1.8, repeat: Infinity }}
                                className="h-2 w-2 rounded-full bg-[#00F5D4]"
                            />
                            <p className="max-w-md text-sm text-[#E8ECF5]">
                                Let Agent 2 cross-reference your niche, demographics and
                                this asset against platform behavior patterns to predict
                                the optimal post slot.
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={run}
                            disabled={loading || balance < 5}
                            className="group inline-flex items-center gap-2 rounded-lg border border-[#1F2A40] bg-[#0B0F19] px-4 py-2 text-sm font-medium text-[#E8ECF5] transition-all hover:border-[#00F5D4]/60 hover:text-[#00F5D4] hover:shadow-[0_0_18px_rgba(0,245,212,0.18)] disabled:cursor-not-allowed disabled:opacity-50"
                            data-testid="agent2-run-btn"
                        >
                            <Sparkles className="h-3.5 w-3.5" />
                            {nicheEmpty
                                ? "Configure niche first"
                                : "Run Audience Predict (−5 NXT)"}
                        </button>
                        {nicheEmpty && (
                            <p className="font-mono text-[11px] italic text-[#8A93A6]">
                                Set your niche in NexaVault for sharper, paid-predictions
                                quality.
                            </p>
                        )}
                    </motion.div>
                )}

                {loading && (
                    <motion.div
                        key="loading"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="flex flex-col gap-3"
                        data-testid="agent2-loading"
                    >
                        <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.32em] text-[#00F5D4]">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            Agent 2 is cross-referencing platform algorithms…
                        </div>
                        <div className="grid gap-2 sm:grid-cols-3">
                            {[0, 1, 2].map((i) => (
                                <div
                                    key={i}
                                    className="sp-shimmer relative h-20 overflow-hidden rounded-lg border border-[#1F2A40] bg-[#0B0F19]"
                                />
                            ))}
                        </div>
                    </motion.div>
                )}

                {result && !loading && (
                    <motion.div
                        key="result"
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex flex-col gap-4"
                        data-testid="agent2-result"
                    >
                        <BestOverall best={result.best_overall} />
                        <div className="grid gap-2">
                            {result.recommendations.map((rec, idx) => (
                                <RecRow
                                    key={`${rec.platform}-${idx}`}
                                    rec={rec}
                                    onUse={() => onUseSlot?.(rec)}
                                    testIdx={idx}
                                />
                            ))}
                        </div>
                        <div className="flex items-center justify-between">
                            <p className="font-mono text-[11px] italic text-[#8A93A6]">
                                Powered by Agent 2 — Audience Predict
                            </p>
                            <button
                                type="button"
                                onClick={run}
                                className="rounded-md border border-[#1F2A40] px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-[#8A93A6] transition-colors hover:border-[#00F5D4]/60 hover:text-[#00F5D4]"
                                data-testid="agent2-rerun-btn"
                            >
                                Re-run (−5 NXT)
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.section>
    );
}

function BestOverall({ best }) {
    return (
        <motion.div
            initial={{ boxShadow: "0 0 0 rgba(0,245,212,0)" }}
            animate={{
                boxShadow: [
                    "0 0 0 rgba(0,245,212,0)",
                    "0 0 32px rgba(0,245,212,0.35)",
                    "0 0 12px rgba(0,245,212,0.15)",
                ],
            }}
            transition={{ duration: 1.4 }}
            className="flex flex-col gap-1 rounded-xl border border-[#00F5D4]/60 bg-[#00F5D4]/8 px-5 py-4"
            style={{ backgroundColor: "rgba(0,245,212,0.06)" }}
            data-testid="agent2-best-overall"
        >
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00F5D4]">
                recommended
            </span>
            <span className="text-lg font-medium text-[#E8ECF5]">
                {PLATFORM_LABEL[best?.platform] || best?.platform || "—"} ·{" "}
                {best?.day_of_week || "—"} at{" "}
                <span className="sp-glow-mint">{best?.time_local || "—"}</span>
            </span>
        </motion.div>
    );
}

function RecRow({ rec, onUse, testIdx }) {
    const Icon = PLATFORM_ICON[rec.platform] || Music2;
    const tint =
        CONFIDENCE_TINT[rec.confidence] || CONFIDENCE_TINT.medium;
    return (
        <div
            className="flex flex-col gap-2 rounded-lg border border-[#1F2A40] bg-[#0B0F19] p-4 sm:flex-row sm:items-center sm:justify-between"
            data-testid={`agent2-rec-${testIdx}`}
            data-platform={rec.platform}
        >
            <div className="flex flex-1 items-start gap-3">
                <Icon className="mt-0.5 h-4 w-4 text-[#8A93A6]" />
                <div className="flex flex-col gap-1">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-[#E8ECF5]">
                            {PLATFORM_LABEL[rec.platform] || rec.platform} ·{" "}
                            {rec.day_of_week} at {rec.time_local}
                        </span>
                        <span
                            className={[
                                "rounded-md border px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider",
                                tint,
                            ].join(" ")}
                            data-testid={`agent2-rec-confidence-${testIdx}`}
                        >
                            {rec.confidence}
                        </span>
                    </div>
                    <p className="max-w-prose text-xs leading-relaxed text-[#8A93A6]">
                        {rec.rationale}
                    </p>
                </div>
            </div>
            <button
                type="button"
                onClick={onUse}
                className="self-start rounded-md border border-[#1F2A40] bg-[#111726] px-3 py-1.5 text-xs font-medium text-[#E8ECF5] transition-all hover:border-[#00F5D4]/60 hover:text-[#00F5D4] sm:self-center"
                data-testid={`agent2-use-${testIdx}`}
            >
                Use this
            </button>
        </div>
    );
}
